/**
 * Copyright (c) 2016-2018 Yega Yazilim ve Bilisim Hizmetleri Dis Tic Ltd Sti
 * All Rights Reserved.
 *
 * The software and data contained herein are confidential and
 * proprietary information of Yega Yazilim ("Confidential Information").
 *
 * Users of this software may not disclose such Confidential Information
 * and shall use it only in accordance with the terms of the license agreement
 * they entered into with Yega Yazilim.
 *
 *
 * Author    Ahmet Dogramaci
 * Date      2018-09-19
 */
import spinner_axios from './spinner-axios'
import no_spinner_axios from './no-spinner-axios';

export default function storeGenerator(basePath,storeConfig,customActions={},no_spinner=false){
    let axios = no_spinner ? no_spinner_axios : spinner_axios;
    const indexCache = {};
    const indexPending = {};
    const showCache = {};
    const showPending = {};

    function stableCacheValue(value) {
        if(Array.isArray(value)){
            return value.map(stableCacheValue);
        }
        if(value && typeof value === 'object'){
            return Object.keys(value).sort().reduce((result, key)=>{
                result[key] = stableCacheValue(value[key]);
                return result;
            }, {});
        }
        return value;
    }

    function cacheKey(value) {
        return JSON.stringify(stableCacheValue(value));
    }

    function hasCachedValue(cache, key) {
        return Object.prototype.hasOwnProperty.call(cache, key);
    }

    function clearCaches() {
        Object.keys(indexCache).forEach((key)=>delete indexCache[key]);
        Object.keys(indexPending).forEach((key)=>delete indexPending[key]);
        Object.keys(showCache).forEach((key)=>delete showCache[key]);
        Object.keys(showPending).forEach((key)=>delete showPending[key]);
    }

    return {
        namespaced: true,
        state: {
            items: [],
            item: {},
            itemsByKey:{}
        },
        mutations: {
            one(state, data) {
                state.item = data;
                return state;
            },
            many(state, data) {
                state.items = data;
                if(storeConfig && storeConfig.index && storeConfig.index.itemStoreKey){
                    for(let i=0; i < data.length; i++){
                        state.itemsByKey[data[i][storeConfig.index.itemStoreKey]]=data[i];
                    }
                }
                return state;
            }
        },
        actions: {
            index(context, query) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    const url = `${basePath}`;
                    let config = {params: query};
                    if(storeConfig && storeConfig.index && storeConfig.index.transformResponse){
                        config['transformResponse']=storeConfig.index.transformResponse;
                    }
                    axios
                        .get(url, config)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('many', response.data);
                    }

                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            index_cached(context, query) {
                const key = cacheKey(query || {});
                if(hasCachedValue(indexCache, key)){
                    return Promise.resolve(indexCache[key]);
                }
                if(indexPending[key]){
                    return indexPending[key];
                }
                indexPending[key] = context.dispatch('index', query)
                    .then((items)=>{
                        indexCache[key] = items;
                        delete indexPending[key];
                        return items;
                    })
                    .catch((error)=>{
                        delete indexPending[key];
                        throw error;
                    });
                return indexPending[key];
            },
            initialize(context,item) {
                return new Promise(initializeItem);

                function initializeItem(resolve, reject) {
                    resolve(item);
                    context.commit('one', item);
                }
            },
            show(context, id) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .get(`${basePath}/${id}`)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                    }
                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            show_cached(context, id) {
                const key = cacheKey(id);
                if(hasCachedValue(showCache, key)){
                    context.commit('one', showCache[key]);
                    return Promise.resolve(showCache[key]);
                }
                if(showPending[key]){
                    return showPending[key];
                }
                showPending[key] = context.dispatch('show', id)
                    .then((item)=>{
                        showCache[key] = item;
                        delete showPending[key];
                        return item;
                    })
                    .catch((error)=>{
                        delete showPending[key];
                        throw error;
                    });
                return showPending[key];
            },
            show_all(context, id) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .get(`${basePath}/${id}/show_all`)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                        clearCaches();
                    }
                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            update(context, item) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .put(`${basePath}/${item.id}`, item)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                        clearCaches();
                    }

                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            clone(context, item) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .put(`${basePath}/${item.id}/duplicate`, item)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                        clearCaches();
                    }

                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            create(context, item) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .post(`${basePath}`, item)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                        clearCaches();
                    }
                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            upload(context, item) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .post(`${basePath}/${item.id}/upload`, item.formData,{
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        })
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        clearCaches();
                    }
                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            upload_collection(context, item) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .post(`${basePath}`, item,{
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        })
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                        clearCaches();
                    }
                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            destroy(context, id) {
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .delete(`${basePath}/${id}`)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', {});
                        clearCaches();
                    }

                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            execute(context, query) {
                let config = {params: query};
                return new Promise(callAxios);

                function callAxios(resolve, reject) {
                    axios
                        .get(`${basePath}/${query.id}`,config)
                        .then(success)
                        .catch(failure);

                    function success(response) {
                        resolve(response.data);
                        context.commit('one', response.data);
                    }
                    function failure(error) {
                        console.log(error.response ? error.response.data : error);
                        reject(error);
                    }
                }
            },
            ...customActions
        }
    };
}
